import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Plus } from "lucide-react";
import { http } from "@/lib/api";
import { daysUntil, fmtDay } from "@/lib/dates";
import { ExamDialog } from "@/components/ExamDialog";
import { logWarn } from "@/lib/log";

function ExamRow({ e, big }) {
  const n = daysUntil(e.date);
  const urgent = n <= 3;
  return (
    <Link to={`/app/exams/${e.exam_id}`} data-testid={`countdown-exam-${e.exam_id}`}
      className={`flex items-center gap-4 rounded-2xl border p-4 hover:-translate-y-0.5 transition-[transform,background-color] ${urgent ? "border-yellow-300/40 bg-yellow-300/[0.06]" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"}`}>
      <div className={`font-display font-semibold leading-none tabular-nums ${big ? "text-5xl" : "text-3xl"} ${urgent ? "text-yellow-300" : "text-sky-300"}`}>{n === 0 ? "!" : n}</div>
      <div className="min-w-0">
        <p className="text-xs text-neutral-500">{n === 0 ? "Exam day. Good luck!" : n === 1 ? "day to go · tomorrow" : `days to go · ${fmtDay(e.date)}`}</p>
        <p className="font-medium truncate">{e.name}</p>
      </div>
    </Link>
  );
}

export function ExamCountdown() {
  const [exams, setExams] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => http.get("/exams").then((r) => setExams(r.data)).catch((e) => logWarn("Exams load failed", e)), []);
  useEffect(() => { load(); }, [load]);
  if (!exams) return null;
  const upcoming = exams.filter((e) => daysUntil(e.date) >= 0).slice(0, 3);
  return (
    <div className="mt-8 space-y-2 rise" data-testid="exam-countdown">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-600 flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Exam countdown</p>
        <div className="flex gap-3 text-xs">
          {exams.length > 0 && <Link to="/app/exams" className="text-neutral-400 hover:text-white" data-testid="countdown-all-exams">All exams</Link>}
          <button onClick={() => setAdding(true)} className="text-sky-400 hover:text-sky-300 flex items-center gap-1" data-testid="countdown-add-exam-btn"><Plus className="h-3 w-3" /> Add exam</button>
        </div>
      </div>
      {!upcoming.length && <p className="text-sm text-neutral-500">Add an exam to see a countdown and get a revision plan.</p>}
      {upcoming.map((e, i) => <ExamRow key={e.exam_id} e={e} big={i === 0} />)}
      {adding && <ExamDialog onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </div>
  );
}
